import React from 'react';
import CallsMixin from './calls-mixin.js';
import Loading from '../bricks/loading.js';
import Error from '../bricks/error.js';
import Lsi from '../bricks/lsi.js';
import Environment from './../environment/environment.js';

export const LoadMixin = {
  //@@viewOn:mixins
  mixins: [
    CallsMixin
  ],
  //@@viewOff:mixins

  //@@viewOn:statics
  statics: {
    UU5_Common_LoadMixin: {
      defaults: {
        minReloadInterval: 10 * 1000, // 10s
        onLoadCall: 'onLoad',
        onReloadCall: 'onReload'
      },
      errors: {
        onLoad: 'Error during loading data from server by call %s.',
        onReload: 'Error during reloading data from server by call %s.'
      },
      lsi: () => (Environment.Lsi.Common.loadMixin)
    }
  },//@@viewOff:statics

  //@@viewOn:propTypes
  propTypes: {
    uri: React.PropTypes.string,
    dtoOut: React.PropTypes.object,
    reloadInterval: React.PropTypes.number
  },
  //@@viewOff:propTypes

  //@@viewOn:getDefaultProps
  getDefaultProps() {
    return {
      uri: null,
      dtoOut: null,
      reloadInterval: 0
    };
  },
  //@@viewOff:getDefaultProps

  //@@viewOn:standardComponentLifeCycle
  getInitialState() {
    return {
      loadFeedback: 'loading',
      dtoOut: null,
      errorDtoOut: null
    };
  },

  componentWillMount() {
    if (this.props.dtoOut) {
      this.onLoadSuccess(this.props.dtoOut);
    }
  },

  componentDidMount() {
    if (!this.props.dtoOut) {
      this.setState({
        loadFeedback: 'loading',
        dtoOut: null,
        errorDtoOut: null
      }, () => this._onLoad(this.props));
    }
  },

  componentWillReceiveProps(nextProps){
    if (nextProps.controlled) {
      this._onLoad(nextProps);
    }
  },

  componentWillUnmount() {
    this._reloadInterval && clearInterval(this._reloadInterval);
  },
  //@@viewOff:standardComponentLifeCycle

  //@@viewOn:interface
  getUri() {
    return this.props.uri;
  },

  getDtoOut() {
    return this.state.dtoOut;
  },

  getErrorData() {
    return this.state.errorDtoOut;
  },

  getLoadFeedback() {
    return this.state.loadFeedback;
  },

  isLoading() {
    return this.getLoadFeedback() === 'loading';
  },

  isReady() {
    return this.getLoadFeedback() === 'ready';
  },

  isError() {
    return this.getLoadFeedback() === 'error';
  },

  onLoadSuccess(dtoOut, setStateCallback) {
    this.setAsyncState({loadFeedback: 'ready', dtoOut: dtoOut, errorDtoOut: null}, setStateCallback);
    return this;
  },

  onReloadSuccess(dtoOut, setStateCallback) {
    this.onLoadSuccess(dtoOut, setStateCallback);
    return this;
  },

  onLoadError(dtoOut, setStateCallback) {
    this.setAsyncState({loadFeedback: 'error', errorDtoOut: dtoOut}, setStateCallback);
    return this;
  },

  onReloadError(dtoOut, setStateCallback) {
    this.onLoadError(dtoOut, setStateCallback);
    return this;
  },

  getLoadFeedbackChildren(getChildren) {
    let children;

    switch (this.getLoadFeedback()) {
      case 'loading':
        children = <Loading />;
        break;
      case 'ready':
        children = getChildren(this.getDtoOut());
        break;
      case 'error':
        children = (
          <Error>
            {this.getLSIComponent('error', 'UU5_Common_LoadMixin')}
          </Error>
        );
        break;
    }

    return children;
  },

  reload(callName, dtoIn) {
    let reloadCall = (callName && this.getCall(callName)) || this._getOnReloadCall();

    if (reloadCall) {
      let reloadDtoIn = dtoIn || this._getReloadDtoIn(this.props);
      reloadCall(reloadDtoIn);
    }

    return this;
  },
  //@@viewOff:interface

  //@@viewOn:componentSpecificHelpers
  _getOnLoadCall() {
    return this.getCall(this.getDefault('onLoadCall', 'UU5_Common_LoadMixin'));
  },

  _getOnReloadCall() {
    let result;

    let callNames = this.constructor.calls;
    if (callNames) {
      let callName = callNames[this.getDefault('onReloadCall', 'UU5_Common_LoadMixin')];

      if (callName) {
        result = this.getCalls()[callName];
      } else {
        result = this._getOnLoadCall();
      }
    } else {
      result = this._getOnLoadCall();
    }

    return result;
  },

  _getLoadDtoIn(props) {
    props = props || this.props;

    let dtoIn = {};

    if (props.uri) {
      dtoIn.uri = props.uri;
    }

    if (typeof this.getOnLoadData_ === 'function') {
      dtoIn.data = this.getOnLoadData_(props);
    }

    dtoIn.done = dtoOut => {
      if (typeof this.onLoadSuccess_ === 'function') {
        this.onLoadSuccess_(dtoOut);
      } else {
        this.onLoadSuccess(dtoOut);
      }
    };

    let callKey = this.getDefault('onLoadCall', 'UU5_Common_LoadMixin');
    dtoIn.fail = dtoOut => {
      this.showError('onLoad', this.getCallName(callKey), {
        mixinName: 'UU5_Common_LoadMixin',
        context: {
          calls: this.getCalls(),
          dtoOut: dtoOut,
          callKey: callKey,
          uri: dtoIn.uri,
          data: dtoIn.data
        }
      });
      if (typeof this.onLoadError_ === 'function') {
        this.onLoadError_(dtoOut);
      } else {
        this.onLoadError(dtoOut);
      }
    };

    return dtoIn;
  },

  _getReloadDtoIn(props) {
    props = props || this.props;
    let dtoIn = {};

    if (props.uri) {
      dtoIn.uri = props.uri;
    }

    let getData = this.getOnReloadData_ || this.getOnLoadData_;
    if (typeof getData === 'function') {
      dtoIn.data = getData(props);
    }

    dtoIn.done = dtoOut => {
      let loadSuccess = this.onReloadSuccess_ || this.onLoadSuccess_;
      if (typeof loadSuccess === 'function') {
        loadSuccess(dtoOut);
      } else {
        this.onReloadSuccess(dtoOut);
      }
    };

    let calls = this.constructor.calls;
    let callKey = this.getDefault('onReloadCall', 'UU5_Common_LoadMixin');
    calls && !calls[callKey] && (callKey = this.getDefault('onLoadCall', 'UU5_Common_LoadMixin'));

    dtoIn.fail = dtoOut => {
      this.showError('onReload', this.getCallName(callKey), {
        mixinName: 'UU5_Common_LoadMixin',
        context: {
          calls: this.getCalls(),
          dtoOut: dtoOut,
          callKey: callKey,
          uri: dtoIn.uri,
          data: dtoIn.data
        }
      });

      let loadError = this.onReloadError_ || this.onLoadError_;
      if (typeof loadError === 'function') {
        loadError(dtoOut);
      } else {
        this.onReloadError(dtoOut);
      }
    };

    return dtoIn;
  },

  _onLoad(props) {
    let loadCall = this._getOnLoadCall();

    if (loadCall) {
      let dtoIn = this._getLoadDtoIn(props);

      this._reloadInterval && clearInterval(this._reloadInterval);
      loadCall(dtoIn);

      let reloadInterval = this.props.reloadInterval || this.getOpt('reloadInterval');
      if (reloadInterval) {
        let reloadCall = this._getOnReloadCall();
        reloadCall = reloadCall || loadCall;

        if (reloadCall) {
          let reloadDtoIn = this._getReloadDtoIn(props);
          this._reloadInterval = setInterval(
            () => reloadCall(reloadDtoIn),
            Math.max(reloadInterval , this.getDefault('minReloadInterval', 'UU5_Common_LoadMixin'))
          );
        }
      }
    }

    return this;
  }
  //@@viewOff:componentSpecificHelpers
};

export default LoadMixin;



// WEBPACK FOOTER //
// ./common/load-mixin.js